import { Inject, Injectable } from '@angular/core';
import { MatchResult, StateRule, UrlService } from '@uirouter/core';
import { Util } from './util';
import { CONFIG_TOKEN } from 'core/constants';
import { IXMOptions } from 'core/interfaces';

/*
    Functions:
    Functions cannot be nested
    There can be multiple different functions in one input string.

        1. Link with link style
        Example: text(Shop Now|https://www.xfinity.com|Shop Xfinity Now)
        Another use case: Inline link with inherited text style except font-color
        Example: Like Xfinity? text(Shop Now|https://www.xfinity.com|Shop Xfinity Now)

        2. Link with primary button style
        Example: pri(Shop Now|https://www.xfinity.com|Shop Xfinity Now)

        3. Link with secondary button style
        Example: sec(Shop Now|https://www.xfinity.com|Shop Xfinity Now)

        4. Text with inherited text style but overwritten with bold font
        Example: bold(Bugs!)

        5. Text with inherited text style but with strikethrough
        Example: strike(Bugs!) Feature!

    Params (can be used in any of the functions above):

        1. Link with accessibility text
        Example: text(Shop Now|https://www.xfinity.com|Shop Xfinity Now)

        2. Link without accessibility text
        Example: text(Shop Now|https://www.xfinity.com)

        3. Link that opens in a new tab
        Example: text(tab|Shop Now|https://www.xfinity.com|Shop Xfinity Now)
*/

export interface TextDecorationInfo {
    newTab?: boolean;
    displayText: string;
    fullUrl?: string;
    a11yText?: string;
    isPlainText: boolean;
    isLink?: boolean;
    isPrimary?: boolean;
    isSecondary?: boolean;
    isBold?: boolean;
    isStrikeThrough?: boolean;
}

export interface LinkDecorationInfo extends TextDecorationInfo{
    uiSref?: string;
    uiParams?: object;
    absoluteUrl?: string;
}

@Injectable({
    providedIn: 'root'
})
export class Decorator {
    private static TEXT_FUNCTION_NAME: string = 'text';
    private static PRI_FUNCTION_NAME: string = 'pri';
    private static SEC_FUNCTION_NAME: string = 'sec';
    private static BOLD_FUNCTION_NAME: string = 'bold';
    private static STRIKE_FUNCTION_NAME: string = 'strike';
    private static ALL_REPLACEABLES: RegExp = /(text|pri|sec|bold|strike)\((.+?)\)/g;
    private static OPEN_IN_NEW_TAB: string = 'tab';
    private static PLAIN_TEXT_SPLITTER: string = '[]';
    private static PARAM_SPLITTER: string = '|';
    private static XM_URL: RegExp = /^https:\/\/www\.xfinity\.com\/mobile(.*)/;
    private static XM_PATH: RegExp = /^\/mobile(.*)/;

    private urlService: UrlService;
    private config: IXMOptions;

    public static transformReplaceable(functionName: string, paramString: string): TextDecorationInfo {
        const replaceableParams: string[] = paramString.split(Decorator.PARAM_SPLITTER);
        const newTab: boolean = replaceableParams[0] === Decorator.OPEN_IN_NEW_TAB;
        const startingIndex: number = newTab ? 1 : 0;
        const isBold: boolean = functionName === Decorator.BOLD_FUNCTION_NAME;
        const isStrikeThrough: boolean = functionName === Decorator.STRIKE_FUNCTION_NAME;

        return {
            newTab,
            displayText: replaceableParams[startingIndex],
            fullUrl: replaceableParams[startingIndex + 1],
            a11yText: replaceableParams[startingIndex + 2],
            isPlainText: isBold || isStrikeThrough,
            isLink: functionName === Decorator.TEXT_FUNCTION_NAME,
            isPrimary: functionName === Decorator.PRI_FUNCTION_NAME,
            isSecondary: functionName === Decorator.SEC_FUNCTION_NAME,
            isBold,
            isStrikeThrough
        };
    }

    constructor(urlService: UrlService, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { urlService, config });
    }

    public parseTextDecoration(input: string): TextDecorationInfo[] {
        if (!Decorator.ALL_REPLACEABLES.test(input)) {
            return [ this.getPlainTextDecorationInfo(input) ];
        }

        const completedDecorationInfo: TextDecorationInfo[] = [];
        const replaceableDecorationInfo: TextDecorationInfo[] = [];

        const stringWithSplitter: string = input.replace(Decorator.ALL_REPLACEABLES, (_replaceableWithFunction: string, functionName: string, paramString: string) => {
            replaceableDecorationInfo.push(Decorator.transformReplaceable(functionName, paramString));

            return Decorator.PLAIN_TEXT_SPLITTER;
        });

        stringWithSplitter.split(Decorator.PLAIN_TEXT_SPLITTER).forEach((plainText: string, index: number) => {
            if (plainText) {
                completedDecorationInfo.push(this.getPlainTextDecorationInfo(plainText));
            }

            if (replaceableDecorationInfo[index]) {
                completedDecorationInfo.push(replaceableDecorationInfo[index]);
            }
        });

        return completedDecorationInfo;
    }

    public parseLinkDecoration(inputString: string): LinkDecorationInfo[] {
        return this.parseTextDecoration(inputString).map((textInfo: TextDecorationInfo) => {
            if (!textInfo.fullUrl) {
                return textInfo;
            }

            const linkInfo: LinkDecorationInfo = textInfo;

            if (Decorator.XM_URL.test(textInfo.fullUrl)) {
                // this will remove the domain and path "/mobile" from the full url
                const fullUrlWithoutBase: string = textInfo.fullUrl.match(Decorator.XM_URL)[1];
                const urlToProcess: URL = new URL(textInfo.fullUrl);

                // includes just the path with no "/mobile" and search/hash params
                const pathWithoutBase: string = urlToProcess.pathname.match(Decorator.XM_PATH)[1];

                // find what part of the URL overlaps with the Base Href of the application
                // ex. /staging/my-account/ with /my-account/devices => /my-account/ as the common portion
                const baseHrefOverlap: string = `${this.config.BASE_URL}@@@${pathWithoutBase}`.match(/(.+)\/?@@@\1/)[1];

                // remove the common portion so we can check the internal router
                // ex. /my-account/devices, replaces /my-account/ with / => /devices
                const spaPath: string = pathWithoutBase.replace(baseHrefOverlap, '/');

                // run matcher on the internal router
                const result: MatchResult = this.urlService.match({
                    path: spaPath, // test internal path without base href
                    search: Util.getObjectFromQueryString(urlToProcess.search),
                    hash: urlToProcess.hash.substring(1)
                });

                const rule: StateRule = <StateRule> result.rule;

                // internal route
                // there is a limitation on matching lazy-loaded routes so we ignore them
                if (result.match && rule.type === 'STATE' && !rule.state.name.includes('**')) {
                    linkInfo.uiSref = rule.state.name;
                    linkInfo.uiParams = result.match;
                } else { // not internal
                    // need to get the root path from the Base Href to build the final URL
                    // ex. incoming URL is /mobile/my-account/devices, we remove /mobile, and replace with root path
                    // ex. Base Href /staging/my-account/ => /staging
                    const baseHrefRoot: string = this.config.BASE_URL === '/' ? '' : this.config.BASE_URL.match(/(\/.+?)\//)[1];

                    linkInfo.absoluteUrl = `${window.location.origin}${baseHrefRoot}${fullUrlWithoutBase}`;
                }
            } else {
                linkInfo.absoluteUrl = textInfo.fullUrl;
            }

            return linkInfo;
        });
    }

    public createCrossAppUrl(fullUrl: string): string {
        if (Decorator.XM_URL.test(fullUrl)) {
            // this will remove the domain and path "/mobile" from the full url
            const fullUrlWithoutBase: string = fullUrl.match(Decorator.XM_URL)[1];

            // need to get the root path from the Base Href to build the final URL
            // ex. incoming URL is /mobile/my-account/devices, we remove /mobile, and replace with root path
            // ex. Base Href /staging/my-account/ => /staging
            const baseHrefRoot: string = this.config.BASE_URL === '/' ? '' : this.config.BASE_URL.match(/(\/.+?)\//)[1];

            return `${window.location.origin}${baseHrefRoot}${fullUrlWithoutBase}`;
        } else {
            return fullUrl;
        }
    }

    private getPlainTextDecorationInfo(text: string): TextDecorationInfo {
        return {
            displayText: text,
            isPlainText: true
        };
    }
}
